import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  MapPinIcon, 
  BuildingOfficeIcon,
  UserGroupIcon,
  FunnelIcon,
  ArrowPathIcon,
  EyeIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  ArrowPathIcon as ReactivateIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const Depots = () => {
  const { user } = useAuth();
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [selectedDepot, setSelectedDepot] = useState(null);
  const [depotInventory, setDepotInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    address: '',
    manager_id: ''
  });
  const [filters, setFilters] = useState({
    search: '',
    location: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });
  const [inventoryPagination, setInventoryPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0
  });

  useEffect(() => {
    fetchDepots();
    if (user?.user_type === 'owner') {
      fetchUsers();
    }
  }, [pagination.page, pagination.per_page, filters, showInactive]);

  const fetchDepots = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        search: filters.search || undefined,
        location: filters.location || undefined,
        show_inactive: showInactive
      };

      // Remove undefined values
      Object.keys(params).forEach(key => 
        params[key] === undefined && delete params[key]
      );

      const response = await api.get('/depot/', { params });
      console.log("Depots response:", response.data);
      setDepots(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      toast.error('Failed to fetch depots');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/', {
        params: { user_type: 'manager', per_page: 1000 }
      });
      setUsers(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDepotInventory = async (depotId) => {
    try {
      setInventoryLoading(true);
      const response = await api.get(`/depot/${depotId}/inventory`, {
        params: {
          page: inventoryPagination.page,
          per_page: inventoryPagination.per_page
        }
      });
      setDepotInventory(response.data.items || []);
      setInventoryPagination(prev => ({
        ...prev,
        total: response.data.total || 0
      }));
    } catch (error) {
      toast.error('Failed to fetch depot inventory');
      console.error(error);
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleCreateDepot = async () => {
    try {
      // Allow empty manager_id by sending null if empty
      const submitData = {
        ...formData,
        manager_id: formData.manager_id === '' ? null : formData.manager_id
      };
      await api.post('/depot/', submitData);
      toast.success('Depot created successfully');
      setShowModal(false);
      resetForm();
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create depot');
    }
  };

  const handleUpdateDepot = async () => {
    try {
      // Allow empty manager_id by sending null if empty
      const submitData = {
        ...formData,
        manager_id: formData.manager_id === '' ? null : formData.manager_id
      };
      await api.put(`/depot/${selectedDepot.id}`, submitData);
      toast.success('Depot updated successfully');
      setShowModal(false);
      resetForm();
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update depot');
    }
  };

  const handleDeactivateDepot = async () => {
    try {
      await api.delete(`/depot/${selectedDepot.id}`);
      toast.success('Depot deactivated successfully');
      setShowDeleteDialog(false);
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to deactivate depot');
    }
  };

  const handleReactivateDepot = async () => {
    try {
      await api.post(`/depot/${selectedDepot.id}/reactivate`);
      toast.success('Depot reactivated successfully');
      setShowReactivateDialog(false);
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reactivate depot');
    }
  };

  const handleViewInventory = async (depot) => {
    setSelectedDepot(depot);
    setInventoryPagination({ page: 1, per_page: 10, total: 0 });
    await fetchDepotInventory(depot.id);
    setShowInventoryModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      address: '',
      manager_id: ''
    });
    setSelectedDepot(null);
  };

  const handleEdit = (depot) => {
    setSelectedDepot(depot);
    setFormData({
      name: depot.name,
      location: depot.location,
      address: depot.address || '',
      manager_id: depot.manager_id || ''
    });
    setShowModal(true);
  };

  const handleDeactivate = (depot) => {
    setSelectedDepot(depot);
    setShowDeleteDialog(true);
  };

  const handleReactivate = (depot) => {
    setSelectedDepot(depot);
    setShowReactivateDialog(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: ''
    });
  };

  const canManageDepots = () => {
    return user?.user_type === 'owner';
  };

  const columns = [
    {
      header: 'Depot Details',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">{row.original.name}</div>
          <div className="text-xs text-gray-500 flex items-center mt-1">
            <MapPinIcon className="h-3 w-3 mr-1" />
            {row.original.location}
          </div>
        </div>
      )
    },
    {
      header: 'Address',
      accessorKey: 'address',
      cell: ({ getValue }) => (
        <div className="text-sm text-gray-600 max-w-xs truncate">
          {getValue() || '-'}
        </div>
      )
    },
    {
      header: 'Manager',
      accessorKey: 'manager_name',
      cell: ({ getValue }) => (
        <div className="flex items-center">
          <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-sm text-gray-900">{getValue() || 'Not Assigned'}</span>
        </div>
      )
    },
    {
      header: 'Status',
      accessorKey: 'is_active',
      cell: ({ getValue }) => (
        <StatusBadge status={getValue() ? 'active' : 'inactive'} />
      )
    },
    {
      header: 'Created',
      accessorKey: 'created_at',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy') : '-'
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewInventory(row.original)}
            className="text-blue-600 hover:text-blue-900"
            title="View Inventory"
          >
            <ArchiveBoxIcon className="h-5 w-5" />
          </button>
          {canManageDepots() && (
            <>
              {row.original.is_active ? (
                <>
                  <button
                    onClick={() => handleEdit(row.original)}
                    className="text-green-600 hover:text-green-900"
                    title="Edit Depot"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeactivate(row.original)}
                    className="text-red-600 hover:text-red-900"
                    title="Deactivate Depot"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleReactivate(row.original)}
                  className="text-blue-600 hover:text-blue-900"
                  title="Reactivate Depot"
                >
                  <ReactivateIcon className="h-5 w-5" />
                </button>
              )}
            </>
          )}
        </div>
      )
    }
  ];

  const inventoryColumns = [
    {
      header: 'Product',
      accessorKey: 'product_name',
      cell: ({ getValue }) => (
        <div className="font-medium text-gray-900">{getValue()}</div>
      )
    },
    {
      header: 'Initial Stock',
      accessorKey: 'initial_quantity_cases',
      cell: ({ getValue }) => (
        <div>
          <div className="text-sm text-gray-900">{getValue() || 0} cases</div>
        </div>
      )
    },
    {
      header: 'Current Stock',
      accessorKey: 'current_quantity_cases',
      cell: ({ getValue, row }) => {
        const value = getValue() || 0;
        const initial = row.original.initial_quantity_cases || 0;
        const isLowStock = value < 10;
        return (
          <div>
            <div className={`text-sm ${isLowStock ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
              {value} cases
            </div>
            {initial > 0 && (
              <div className="text-xs text-gray-500">
                ({row.original.utilization || 0} cases used)
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: 'Utilization',
      accessorKey: 'utilization_percent',
      cell: ({ getValue }) => {
        const percent = getValue() || 0;
        return (
          <div className="flex items-center space-x-2">
            <div className="w-24 bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${percent > 75 ? 'bg-red-500' : percent > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(percent, 100)}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-600">{percent}%</span>
          </div>
        );
      }
    },
    {
      header: 'Bottles',
      accessorKey: 'quantity_bottles',
      cell: ({ getValue }) => (
        <span className="text-sm text-gray-600">{getValue() || 0} bottles</span>
      )
    },
    {
      header: 'Last Updated',
      accessorKey: 'last_updated',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy HH:mm') : '-'
    }
  ];

  // Get unique locations for filter dropdown
  const uniqueLocations = [...new Set(depots.map(d => d.location).filter(Boolean))];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Depot Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage depots, locations, and depot inventory.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          {canManageDepots() && (
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Depot
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <BuildingOfficeIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Depots</dt>
                  <dd className="text-lg font-medium text-gray-900">{pagination.total}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Depots</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {depots.filter(d => d.is_active).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                <EyeSlashIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Inactive Depots</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {depots.filter(d => !d.is_active).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-orange-500 rounded-md p-3">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Locations</dt>
                  <dd className="text-lg font-medium text-gray-900">{uniqueLocations.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Show Inactive Toggle */}
      <div className="mt-4 flex justify-end">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Show inactive depots</span>
        </label>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Depots</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Search</label>
              <input
                type="text"
                placeholder="Search by name or location..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
              >
                <option value="">All Locations</option>
                {uniqueLocations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchDepots}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Depots Table */}
      <div className="mt-8">
        <DataTable
          data={depots}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Create/Edit Depot Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={selectedDepot ? 'Edit Depot' : 'Create New Depot'}
        size="lg"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          selectedDepot ? handleUpdateDepot() : handleCreateDepot();
        }}>
          <div className="space-y-4">
            <div>
              <label className="form-label">Depot Name *</label>
              <div className="relative">
                <BuildingOfficeIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  required
                  className="form-input pl-10"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter depot name"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Location *</label>
              <div className="relative">
                <MapPinIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  required
                  className="form-input pl-10"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="City or town"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Address</label>
              <textarea
                className="form-input"
                rows="3"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Full street address"
              />
            </div>

            <div>
              <label className="form-label">Depot Manager (Optional)</label>
              <select
                className="form-input"
                value={formData.manager_id}
                onChange={(e) => setFormData({...formData, manager_id: e.target.value})}
              >
                <option value="">-- No Manager Assigned --</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.email})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Assign a manager to oversee this depot (optional)
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {selectedDepot ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Depot Inventory Modal */}
      <Modal
        isOpen={showInventoryModal}
        onClose={() => {
          setShowInventoryModal(false);
          setDepotInventory([]);
        }}
        title={`Inventory - ${selectedDepot?.name}`}
        size="xl"
      >
        {inventoryLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : depotInventory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ArchiveBoxIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p>No inventory found for this depot</p>
          </div>
        ) : (
          <div>
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-600">Total Products</p>
                <p className="text-xl font-bold text-blue-700">{depotInventory.length}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-green-600">Total Cases</p>
                <p className="text-xl font-bold text-green-700">
                  {depotInventory.reduce((sum, item) => sum + (item.current_quantity_cases || 0), 0)}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-xs text-orange-600">Avg Utilization</p>
                <p className="text-xl font-bold text-orange-700">
                  {Math.round(depotInventory.reduce((sum, item) => sum + (item.utilization_percent || 0), 0) / depotInventory.length)}%
                </p>
              </div>
            </div>

            {/* Inventory Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Initial Stock</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current Stock</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilization</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bottles</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {depotInventory.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.product_name}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">{item.initial_quantity_cases || 0}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className={item.current_quantity_cases < 10 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                          {item.current_quantity_cases || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center space-x-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${item.utilization_percent > 75 ? 'bg-red-500' : item.utilization_percent > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(item.utilization_percent, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600">{item.utilization_percent}%</span>
                        </div>
                       </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">{item.quantity_bottles || 0} </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {item.last_updated ? format(new Date(item.last_updated), 'dd MMM yyyy HH:mm') : '-'}
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Inventory Pagination */}
            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-gray-500">
                Showing {depotInventory.length} of {inventoryPagination.total} items
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setInventoryPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }));
                    fetchDepotInventory(selectedDepot.id);
                  }}
                  disabled={inventoryPagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => {
                    setInventoryPagination(prev => ({ ...prev, page: prev.page + 1 }));
                    fetchDepotInventory(selectedDepot.id);
                  }}
                  disabled={inventoryPagination.page * inventoryPagination.per_page >= inventoryPagination.total}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Deactivate Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeactivateDepot}
        title="Deactivate Depot"
        message={`Are you sure you want to deactivate ${selectedDepot?.name}? This will prevent new consignments from being assigned to this depot. You can reactivate it later.`}
        confirmText="Deactivate"
        type="danger"
      />

      {/* Reactivate Confirmation */}
      <ConfirmDialog
        isOpen={showReactivateDialog}
        onClose={() => setShowReactivateDialog(false)}
        onConfirm={handleReactivateDepot}
        title="Reactivate Depot"
        message={`Are you sure you want to reactivate ${selectedDepot?.name}? This will allow new consignments to be assigned to this depot again.`}
        confirmText="Reactivate"
        type="success"
      />
    </div>
  );
};

export default Depots;